"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Button } from "./ui/Button"
import { Trash } from "./icons/Trash"
import { ChevronDown } from "./icons/ChevronDown"
import { ChevronRight } from "./icons/ChevronRight"
import { PlusCircle } from "./icons/PlusCircle"

interface JsonNodeProps {
  propertyKey: string
  value: any
  onChange: (value: any) => void
  onRemove: () => void
  depth?: number
}

export const JsonNode: React.FC<JsonNodeProps> = ({ propertyKey, value, onChange, onRemove, depth = 0 }) => {
  const [expanded, setExpanded] = useState(true)
  const [valueType, setValueType] = useState<string>(getType(value))
  const [newNestedKey, setNewNestedKey] = useState("")

  useEffect(() => {
    setValueType(getType(value))
  }, [value])

  function getType(val: any): string {
    if (val === null) return "null"
    if (Array.isArray(val)) return "array"
    return typeof val
  }

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value
    setValueType(newType)

    let newValue
    switch (newType) {
      case "string":
        newValue = ""
        break
      case "number":
        newValue = 0
        break
      case "boolean":
        newValue = false
        break
      case "object":
        newValue = {}
        break
      case "array":
        newValue = []
        break
      case "null":
        newValue = null
        break
      default:
        newValue = ""
    }

    onChange(newValue)
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value

    if (valueType === "number") {
      newValue = Number(newValue)
    } else if (valueType === "boolean") {
      newValue = e.target.value === "true"
    }

    onChange(newValue)
  }

  const handleAddNestedProperty = () => {
    if (!newNestedKey.trim()) {
      alert("Please enter a property name")
      return
    }

    if (valueType === "object") {
      if (value[newNestedKey] !== undefined) {
        alert("Property already exists")
        return
      }

      onChange({
        ...value,
        [newNestedKey]: "",
      })
    } else if (valueType === "array") {
      onChange([...value, ""])
    }

    setNewNestedKey("")
  }

  const handleUpdateNestedProperty = (key: string | number, newValue: any) => {
    if (valueType === "object") {
      onChange({
        ...value,
        [key]: newValue,
      })
    } else if (valueType === "array") {
      const newArray = [...value]
      newArray[key as number] = newValue
      onChange(newArray)
    }
  }

  const handleRemoveNestedProperty = (key: string | number) => {
    if (valueType === "object") {
      const newObj = { ...value }
      delete newObj[key as string]
      onChange(newObj)
    } else if (valueType === "array") {
      onChange(value.filter((_: any, i: number) => i !== key))
    }
  }

  const renderValueEditor = () => {
    switch (valueType) {
      case "string":
        return (
          <input
            type="text"
            value={value}
            onChange={handleValueChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        )
      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={handleValueChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        )
      case "boolean":
        return (
          <select
            value={String(value)}
            onChange={handleValueChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        )
      case "object":
      case "array":
        return (
          <div className="mt-2 space-y-3">
            {expanded && (
              <>
                <div className="flex items-center space-x-2">
                  {valueType === "object" && (
                    <input
                      type="text"
                      value={newNestedKey}
                      onChange={(e) => setNewNestedKey(e.target.value)}
                      placeholder="New property name"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                  )}
                  <Button onClick={handleAddNestedProperty} variant="secondary" size="sm">
                    <PlusCircle className="w-4 h-4 mr-1" />
                    Add {valueType === "array" ? "Item" : "Property"}
                  </Button>
                </div>

                <div className="pl-4 border-l-2 border-gray-300 space-y-3">
                  {valueType === "object" ? (
                    Object.keys(value).length === 0 ? (
                      <div className="text-gray-500 italic">Empty object</div>
                    ) : (
                      Object.keys(value).map((key) => (
                        <JsonNode
                          key={key}
                          propertyKey={key}
                          value={value[key]}
                          onChange={(newValue) => handleUpdateNestedProperty(key, newValue)}
                          onRemove={() => handleRemoveNestedProperty(key)}
                          depth={depth + 1}
                        />
                      ))
                    )
                  ) : value.length === 0 ? (
                    <div className="text-gray-500 italic">Empty array</div>
                  ) : (
                    value.map((item: any, index: number) => (
                      <JsonNode
                        key={index}
                        propertyKey={`[${index}]`}
                        value={item}
                        onChange={(newValue) => handleUpdateNestedProperty(index, newValue)}
                        onRemove={() => handleRemoveNestedProperty(index)}
                        depth={depth + 1}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )
      case "null":
        return <div className="text-gray-500 italic">null</div>
      default:
        return null
    }
  }

  return (
    <div className="border border-gray-300 rounded-md p-3 bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {(valueType === "object" || valueType === "array") && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-gray-500 hover:text-gray-700"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          <span className="font-medium text-blue-600">{propertyKey}</span>
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={valueType}
            onChange={handleTypeChange}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md"
          >
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="object">Object</option>
            <option value="array">Array</option>
            <option value="null">Null</option>
          </select>

          <Button onClick={onRemove} variant="danger" size="sm">
            <Trash className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {renderValueEditor()}
    </div>
  )
}
